import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function customerLabel(customer) {
  const extras = [];
  if (customer.vat_id) extras.push(customer.vat_id);
  if (customer.address) {
    extras.push(customer.address.replace(/\s+/g, ' ').slice(0, 48));
  }
  return extras.length ? `${customer.name} — ${extras.join(' · ')}` : customer.name;
}

function matchCustomers(customers, query, { allowEmpty = false } = {}) {
  const q = (query || '').trim().toLowerCase();
  if (!q) {
    return allowEmpty ? customers.slice(0, 12) : [];
  }
  return customers
    .filter((customer) => {
      const haystack = `${customer.name || ''} ${customer.address || ''} ${customer.vat_id || ''}`.toLowerCase();
      return haystack.includes(q);
    })
    .slice(0, 12);
}

function CustomerPicker({ customers, selectedId, onSelect, disabled }) {
  return (
    <div className="form-group">
      <div className="customer-picker-header">
        <label className="label">Gespeicherten Kunden wählen</label>
        <Link to="/customers" className="customer-manage-link">Kunden verwalten</Link>
      </div>
      <select
        className="input"
        value={selectedId || ''}
        onChange={(e) => {
          const id = e.target.value ? Number(e.target.value) : '';
          const customer = customers.find((c) => c.id === id) || null;
          onSelect(id || '', customer);
        }}
        disabled={disabled || customers.length === 0}
      >
        <option value="">
          {customers.length === 0
            ? '— Noch keine Kunden gespeichert —'
            : '— Manuell eingeben oder Kunden wählen —'}
        </option>
        {customers.map((customer) => (
          <option key={customer.id} value={customer.id}>
            {customerLabel(customer)}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Name field with typeahead: focus or typing shows saved customers;
 * selecting one fills name / address / VAT via onSelectCustomer.
 */
function CustomerNameInput({
  value,
  onChange,
  customers,
  onSelectCustomer,
  disabled,
  placeholder,
  required,
  id,
  label = 'Firma / Name *',
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const listId = id ? `${id}-suggestions` : 'customer-name-suggestions';

  const matches = matchCustomers(customers, value, { allowEmpty: true });
  const showList = open && customers.length > 0 && matches.length > 0;
  const showEmpty = open && customers.length === 0;

  useEffect(() => {
    setHighlight(0);
  }, [value, open]);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const pickCustomer = (customer) => {
    if (!customer) return;
    onSelectCustomer(customer.id, customer);
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (!showList) {
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && matches.length > 0) {
        setOpen(true);
        event.preventDefault();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((i) => (i + 1) % matches.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((i) => (i - 1 + matches.length) % matches.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      pickCustomer(matches[highlight]);
    }
  };

  return (
    <div className="form-group customer-name-autocomplete" ref={wrapRef}>
      <div className="customer-picker-header">
        <label className="label" htmlFor={id}>{label}</label>
        <Link to="/customers" className="customer-manage-link">Kunden verwalten</Link>
      </div>
      <div className="customer-name-input-wrap">
        <input
          id={id}
          type="text"
          className="input customer-name-input"
          value={value}
          disabled={disabled}
          placeholder={placeholder || 'Tippen oder klicken — gespeicherte Kunden erscheinen'}
          required={required}
          autoComplete="off"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList ? `${listId}-option-${highlight}` : undefined}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="customer-name-toggle"
          tabIndex={-1}
          aria-label="Kundenvorschläge anzeigen"
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            setOpen((wasOpen) => !wasOpen);
          }}
        >
          ▾
        </button>
      </div>
      {showList && (
        <ul id={listId} className="customer-suggestions" role="listbox">
          {matches.map((customer, index) => (
            <li
              key={customer.id}
              id={`${listId}-option-${index}`}
              role="option"
              aria-selected={index === highlight}
              className={`customer-suggestion${index === highlight ? ' is-active' : ''}`}
              onMouseEnter={() => setHighlight(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                pickCustomer(customer);
              }}
            >
              <span className="customer-suggestion-name">{customer.name}</span>
              {(customer.vat_id || customer.address) && (
                <span className="customer-suggestion-meta">
                  {[customer.vat_id, customer.address && customer.address.replace(/\s+/g, ' ').slice(0, 60)]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {showEmpty && (
        <div className="customer-suggestions customer-suggestions-empty">
          Noch keine Kunden gespeichert.{' '}
          <Link to="/customers">Kunden anlegen</Link>
        </div>
      )}
    </div>
  );
}

export { CustomerNameInput, customerLabel };
export default CustomerPicker;
